import {
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  faCopy,
  faInbox,
  faInfoCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';

import { injectQuery } from '@tanstack/angular-query-experimental';

import {
  DialogCloseReason,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
} from '@sui/dialog';
import { SimpleDialogBase } from 'src/app/shared/abstract/simple-dialog-base';
import { ToastService } from '@services/toast.service';

import {
  formatJsonValue,
  hasJsonContent,
  isJsonContainer,
  resolveJsonPath,
} from './json-value.utils';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SuiButtonComponent } from '@sui/button';
import { LoadingComponent } from '../loading/loading.component';
import { NgpLabel } from 'ng-primitives/form-field';
import { SuiLabelDirective } from '@sui/form-field';
import { JsonTreeComponent } from './json-tree/json-tree.component';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Lazily fetched payload — Angular Query cache key + fetcher.
 * Build it with a factory from `src/app/api/queries` (e.g. `userActionLogExtraMetadataQuerySource`).
 */
export interface JsonViewerDialogSource {
  queryKey: unknown[];
  fetch: () => Promise<unknown>;
}

/**
 * A field the consumer wants surfaced above the full payload.
 * `path` is dotted (`ext_log.record.http_error_message`); missing paths are skipped.
 */
export interface JsonViewerPinnedField {
  label: string;
  path: string;
}

export interface JsonViewerDialogData {
  /** already-translated dialog title */
  title: string;
  /** payload the consumer already has at hand */
  json?: unknown;
  /** payload the dialog has to fetch — takes precedence over `json` */
  source?: JsonViewerDialogSource;
  /** optional curated fields shown on top; the full payload is always rendered below */
  pinned?: JsonViewerPinnedField[];
}

interface JsonViewerDialogOutput {}

@Component({
  selector: 'app-json-viewer-dialog',
  templateUrl: './json-viewer-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './json-viewer-dialog.component.scss',
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    FaIconComponent,
    SuiButtonComponent,
    SuiDialogContentSlot,
    LoadingComponent,
    NgpLabel,
    SuiLabelDirective,
    JsonTreeComponent,
    SuiDialogFooterSlot,
    TranslatePipe,
  ],
})
export class JsonViewerDialogComponent extends SimpleDialogBase<
  JsonViewerDialogData,
  JsonViewerDialogOutput
> {
  private toastService = inject(ToastService);

  dialogTitle = this.dialogData.title;
  actionIcon = faInfoCircle;

  icons = {
    copy: faCopy,
    inbox: faInbox,
    warning: faExclamationTriangle,
  };

  declare protected dirtyHandler: never;

  private source = this.dialogData.source;

  private sourceQuery = injectQuery(() => ({
    queryKey: this.source?.queryKey ?? ['json-viewer', 'inline'],
    queryFn: () => this.source?.fetch() ?? Promise.resolve(null),
    enabled: !!this.source,
    retry: 3,
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  }));

  isLoading = computed(() => !!this.source && this.sourceQuery.isPending());
  hasError = computed(() => !!this.source && this.sourceQuery.isError());

  json = computed(() =>
    this.source ? this.sourceQuery.data() : this.dialogData.json,
  );

  hasJson = computed(() => hasJsonContent(this.json()));

  /** a scalar payload has no tree to walk — printed as a single block instead */
  isTreePayload = computed(() => isJsonContainer(this.json()));

  scalarText = computed(() => formatJsonValue(this.json()));

  pinnedFields = computed(() => {
    const payload = this.json();
    const pinned = this.dialogData.pinned ?? [];

    return pinned
      .map(({ label, path }) => ({
        label,
        value: resolveJsonPath(payload, path),
      }))
      .filter(({ value }) => hasJsonContent(value))
      .map(({ label, value }) => {
        const text = formatJsonValue(value);
        return { label, text, multiline: text.includes('\n') };
      });
  });

  public cancel(): void {
    this.close({ reason: DialogCloseReason.Cancel });
  }

  public copyToClipboard(): void {
    if (!this.hasJson()) return;

    const payload = this.json();
    const text =
      typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);

    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.toastService.showSuccess(
          this.translateService.instant('json-viewer.copied'),
        );
      })
      .catch(() => {
        this.toastService.showError(
          this.translateService.instant('json-viewer.copy-failed'),
        );
      });
  }
}
